import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'index';
import ResourceList from 'lib/jsonApi/ResourceList';
import { resetStore } from '../app/slice';
import { GenericActionPayload } from 'types/redux-types';
import { PanelDetailReagent } from 'types/reagents';

type TemplatesState = {
  workflowTemplates: ResourceList;
  procedureTemplates: ResourceList;
  customProcedureTemplates: ResourceList;
  bdProcedureTemplates: ResourceList;
};

const initialState: TemplatesState = {
  workflowTemplates: new ResourceList(),
  procedureTemplates: new ResourceList(),
  customProcedureTemplates: new ResourceList(),
  bdProcedureTemplates: new ResourceList(),
};

export type CreateWorkflowFromTemplateValues = {
  name: string,
  panel_id: string,
  project_id: number,
  reagents: (Partial<PanelDetailReagent> | null)[],
  cytometer_profile_id?: number,
}

const saveProcedureAsTemplate = createAction('saveProcedureAsTemplate');
const requestProcedureTemplates = createAction('requestProcedureTemplates');
const requestCustomProcedureTemplates = createAction('requestCustomProcedureTemplates');
const requestBdProcedureTemplates = createAction('requestBdProcedureTemplates');
const saveWorkflowAsTemplate = createAction('saveWorkflowAsTemplate');
const saveWorkflowAsStudy = createAction('saveWorkflowAsStudy');
const requestWorkflowTemplates = createAction('requestWorkflowTemplates');
const deleteWorkflowTemplate = createAction('deleteWorkflowTemplate');
const createWorkflowFromTemplate = createAction<GenericActionPayload & {
  panelAsBase?: boolean,
  values: CreateWorkflowFromTemplateValues,
  selectedGroup: number,
}>('createWorkflowFromTemplate');

export const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    setWorkflowTemplates: (state, action: PayloadAction<ResourceList>) => { state.workflowTemplates = action.payload; },
    setProcedureTemplates: (state, action: PayloadAction<ResourceList>) => { state.procedureTemplates = action.payload; },
    setCustomProcedureTemplates: (state, action: PayloadAction<ResourceList>) => { state.customProcedureTemplates = action.payload; },
    setBdProcedureTemplates: (state, action: PayloadAction<ResourceList>) => { state.bdProcedureTemplates = action.payload; },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStore.type, () => initialState);
  },
});

const { setWorkflowTemplates, setProcedureTemplates, setCustomProcedureTemplates, setBdProcedureTemplates } = templatesSlice.actions;

// Actions
export {
  saveProcedureAsTemplate,
  requestProcedureTemplates,
  requestCustomProcedureTemplates,
  requestBdProcedureTemplates,
  saveWorkflowAsTemplate,
  saveWorkflowAsStudy,
  requestWorkflowTemplates,
  deleteWorkflowTemplate,
  createWorkflowFromTemplate,
  setWorkflowTemplates,
  setProcedureTemplates,
  setCustomProcedureTemplates,
  setBdProcedureTemplates,
};

// Selectors

export const getWorkflowTemplates = (state: RootState) => state.templates.workflowTemplates;
export const getProcedureTemplates = (state: RootState) => state.templates.procedureTemplates;
export const getCustomProcedureTemplates = (state: RootState) => state.templates.customProcedureTemplates;
export const getBdProcedureTemplates = (state: RootState) => state.templates.bdProcedureTemplates;

// Reducer
export default templatesSlice.reducer;
